/**
 * What the course costs, and redeeming a code instead of paying for it.
 *
 * Two calls to the Worker, kept beside each other because they are two halves
 * of one question the page asks on the way in: what is the offer, and did
 * this person's code work.
 *
 * The price is fetched rather than bundled. It lives in the Worker's
 * environment so that changing it is a config deploy instead of a rebuild
 * (worker/stripe.ts), which means the tab genuinely cannot know it and has to
 * ask. The cost is one small request on the landing page; the alternative is
 * a price baked into a bundle, which is the version where the number on the
 * page and the number that gets charged are allowed to disagree — and they
 * are the same two values, since the amount the Worker quotes here is the
 * amount it puts on the Checkout Session it creates.
 *
 * Nothing here decides whether anybody owns anything. `redeemCode` reports
 * what the Worker said and the catalogue is then re-read from the one place
 * that knows (`usePaid`), exactly as the checkout return does — a client that
 * concluded "owned" from its own successful-looking POST would be a client
 * that can be made to conclude it.
 */

/** The offer, as the Worker describes it. */
export interface Offer {
  /** The price in the currency's smallest unit, or null if this deployment has not set one. */
  price: number | null;
  /**
   * ISO 4217, e.g. `USD`, and upper-case: that is the spelling the Worker
   * keeps and the one `Intl` wants on this side (lib/format.ts). Stripe's own
   * API is the odd one out and lower-cases it on the way out, which is a
   * detail that lives entirely in worker/stripe.ts and never reaches here.
   *
   * Null exactly when `price` is.
   */
  currency: string | null;
  /** Whether a checkout can actually be started right now. */
  payable: boolean;
  /** Whether this deployment has any invite codes configured. */
  invite: boolean;
  /**
   * During a sale, the regular price `price` is instead of, and the ISO
   * instant the sale ends (worker/stripe.ts, `coursePrice`). Both null
   * otherwise. `price` is always what the checkout charges.
   */
  regular: number | null;
  saleUntil: string | null;
  /**
   * One month instead of three (owner, 2026-09-29), in the same currency and
   * under the same `saleUntil`: `price` is what its checkout charges and
   * `regular` what is struck through beside it during the sale. Null when the
   * deployment sells only the three months.
   */
  month: { price: number; regular: number | null } | null;
  /**
   * The reader's own currency and its rate against `currency`, for showing
   * the prices above as an approximation in their money (lib/format.ts,
   * `approxMoney`). Null when there is nothing to convert — the reader
   * already uses `currency`, or the Worker could not tell where they are or
   * what the rate is (worker/local-currency.ts). It never changes what is
   * charged, which is `price` in `currency`.
   */
  local: LocalCurrency | null;
  /**
   * Where every price above is a regional one (worker/regional-price.ts,
   * docs/regional-pricing.md): the reader's country, ISO 3166-1 alpha-2, and
   * its tier. The figures are already the regional ones, so this is only for
   * saying so. Null is the whole price.
   */
  region: { country: string; tier: 'middle' | 'low' } | null;
}

/**
 * The reader's currency for converting an amount in `currency`, or null. The
 * rate is against the offer's currency only, so an amount in any other one
 * (a brief's fee, some day) is shown as it is rather than converted wrongly.
 */
export function localFor(offer: Offer | null, currency: string): LocalCurrency | null {
  return offer?.local && offer.currency === currency ? offer.local : null;
}

/** A currency and how many of it one unit of the offer's currency buys. */
export interface LocalCurrency {
  currency: string;
  rate: number;
}

/**
 * The offer, or null if the Worker could not be asked.
 *
 * Null rather than a default, and the difference matters at the call site: a
 * page that invented `payable: false` on a failed request would tell a
 * visitor the course cannot be bought because their wifi dropped. Null means
 * "not known", and the offer block stays out of the way until it is.
 */
export function fetchOffer(): Promise<Offer | null> {
  // One request per page load for every block that shows a price. The offer
  // changes only on deploy, and the reader's currency not at all within a
  // visit. A failure is not kept, so the next caller asks again.
  if (!pending) {
    pending = askForOffer().then((offer) => {
      if (!offer) pending = null;
      return offer;
    });
  }
  return pending;
}

let pending: Promise<Offer | null> | null = null;

async function askForOffer(): Promise<Offer | null> {
  let res: Response;
  try {
    res = await fetch('/api/offer', { credentials: 'same-origin' });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return null;
  }
  const b = body as Partial<Offer> | null;
  if (!b || typeof b !== 'object') return null;
  // Read field by field rather than trusted whole. This is the Worker's own
  // answer and not a stranger's, but it is also the number printed next to a
  // button that charges money, and a string where an integer was expected
  // should be no price rather than `NaN` on the page.
  const price = typeof b.price === 'number' && Number.isInteger(b.price) && b.price > 0 ? b.price : null;
  const currency = typeof b.currency === 'string' && b.currency ? b.currency : null;
  return {
    price: currency ? price : null,
    currency: price ? currency : null,
    payable: b.payable === true,
    invite: b.invite === true,
    // Only a well-formed country and tier is said on the page; anything else
    // is drawn as the whole price would be, with no line naming a country.
    region: price && currency && b.region && typeof b.region === 'object'
      && typeof b.region.country === 'string' && /^[A-Z]{2}$/.test(b.region.country)
      && (b.region.tier === 'middle' || b.region.tier === 'low')
      ? { country: b.region.country, tier: b.region.tier }
      : null,
    // A sale needs both halves and a regular price above the one charged;
    // anything else is drawn as one plain price, never as a discount.
    ...(() => {
      const regular = typeof b.regular === 'number' && Number.isInteger(b.regular) ? b.regular : null;
      const saleUntil = typeof b.saleUntil === 'string' && Number.isFinite(Date.parse(b.saleUntil)) ? b.saleUntil : null;
      return price && regular && saleUntil && regular > price ? { regular, saleUntil } : { regular: null, saleUntil: null };
    })(),
    // The same rules for the month, and no month without the three months
    // it is offered beside. Its strikethrough needs the sale's end, which is
    // the three months' `saleUntil`, so a month on sale alone is drawn plain.
    month: (() => {
      const m = b.month as { price?: unknown; regular?: unknown } | null | undefined;
      if (!price || !currency || !m || typeof m.price !== 'number' || !Number.isInteger(m.price) || m.price <= 0) return null;
      const saleUntil = typeof b.saleUntil === 'string' && Number.isFinite(Date.parse(b.saleUntil));
      const regular = typeof m.regular === 'number' && Number.isInteger(m.regular) && m.regular > m.price && saleUntil
        ? m.regular : null;
      return { price: m.price, regular };
    })(),
    // A well-formed currency code and a rate that is a number above zero,
    // or no conversion at all: a malformed `local` must cost the reader the
    // translation, never the price.
    local: (() => {
      const l = b.local as Partial<LocalCurrency> | null | undefined;
      if (!price || !currency || !l || typeof l.currency !== 'string' || !/^[A-Z]{3}$/.test(l.currency)) return null;
      if (l.currency === currency || typeof l.rate !== 'number' || !Number.isFinite(l.rate) || l.rate <= 0) return null;
      return { currency: l.currency, rate: l.rate };
    })(),
  };
}

/**
 * What happened to a code.
 *
 * `malformed` and `unknown` are kept apart because they are different things
 * to tell somebody. One is "check what you typed", which is worth acting on;
 * the other is "that is not a code we have", which is not a typo and should
 * not send them back to re-read the same string. The Worker draws the line at
 * the shape of the code and never at whether a well-formed code is live, so
 * this distinction leaks nothing about which codes exist.
 *
 * `spent` is the third, and it is the one refusal that does say something
 * about a real code: this was ours, and it has been used. That is owed to the
 * person holding it — it is the difference between "try again" and "this is
 * gone, ask for another" — and it is not worth anything to somebody probing,
 * who would have had to guess a 260-bit string to be told it.
 */
export type RedeemResult =
  /** It worked. `already` means this account owned the course before the code was typed. */
  | { state: 'redeemed'; already: boolean }
  /** Not the shape of a code at all — very likely a typo or a half-copied string. */
  | { state: 'malformed' }
  /** A well-formed code that is not one of ours, or is no longer live. */
  | { state: 'unknown' }
  /** A single-use code that has already unlocked the course for somebody. */
  | { state: 'spent' }
  /** There has to be an account to unlock. */
  | { state: 'anonymous' }
  /** This deployment has no codes configured, so the route does not exist. */
  | { state: 'unavailable' }
  /** The Worker could not be reached, or answered with something unusable. */
  | { state: 'failed' };

export async function redeemCode(code: string): Promise<RedeemResult> {
  let res: Response;
  try {
    res = await fetch('/api/invite', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { state: 'failed' };
  }

  if (res.status === 401) return { state: 'anonymous' };
  // 404 is the Worker saying the route is not there, which is what a
  // deployment with no codes configured looks like (worker/invite.ts).
  if (res.status === 404) return { state: 'unavailable' };

  if (res.status === 403) {
    // The Worker distinguishes the refusals in the body; a body that does not
    // arrive or does not say is treated as the least helpful of them, because
    // telling somebody to check for a typo in a code that is simply not live
    // sends them round a loop they cannot get out of.
    let reason: unknown;
    try {
      reason = ((await res.json()) as { reason?: unknown }).reason;
    } catch {
      reason = undefined;
    }
    if (reason === 'malformed') return { state: 'malformed' };
    if (reason === 'spent') return { state: 'spent' };
    return { state: 'unknown' };
  }

  if (!res.ok) return { state: 'failed' };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { state: 'failed' };
  }
  const b = body as { ok?: unknown; already?: unknown } | null;
  if (b?.ok !== true) return { state: 'failed' };
  return { state: 'redeemed', already: b.already === true };
}
